import React, { useContext, useEffect, useState } from 'react';
import Ownership from 'src/models/tract_detail/Ownership';
import PagedResultSet from 'src/models/common/PagedResultSet';
import OwnershipContactCategoryForm from 'src/models/OwnershipContactCategoryForm';
import ProjectStaff from 'src/models/ProjectStaff';
import Dialog from 'src/components/common/Dialog.redesign';
import ContactNote from 'src/models/ContactNote';
import LeaseGenerationOwnerData from 'src/models/leasing/LeaseGenerationOwnerData';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';
import OwnershipTractList from '../../TractComponents/OwnershipTractList';
import ContactNoteForm from '../../TractComponents/ContactNoteForm';
import LeaseBuyerForm from '../../TractComponents/LeaseBuyerForm';
import { AnyObject } from 'react-final-form';
import ContactCategoryForm from '../../TractComponents/ContactCategoryForm';
import County from 'src/models/County';
import OwnershipLeaseBuyer, { OwnershipLeaseBuyerForm } from 'src/models/OwnershipLeaseBuyer';

interface IManageOwnershipDialogStateProps {
    ownership: Ownership;
    projectType: 'Mineral' | 'Surface';
    context: 'Lease Buyers' | 'Contact Categories' | 'Contact Notes';
    createNewNote?: boolean;
    projectStaff?: PagedResultSet<ProjectStaff>;
    contactCategories?: OwnershipContactCategoryForm;
    contactNote?: ContactNote;

    handleSendList?: (sendList: Array<OwnershipLeaseBuyer>) => void;
    refreshContainer?: () => void;
    onCloseDialog?: () => void;
}

const ManageOwnershipDialog: React.FC<IManageOwnershipDialogStateProps> = (props: IManageOwnershipDialogStateProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    
    const { get, response, post, put } = useFetch();

    const [contactNote, setContactNote] = useState<ContactNote>(props.contactNote);
    const [ownerDetail, setOwnerDetail] = useState<LeaseGenerationOwnerData>(null);
    const [selectedOwnershipIds, setSelectedOwnershipIds] = useState<Array<number>>([]);
    const [saveComplete, setSaveComplete] = useState<boolean>(false);
    const [leaseBuyers, setLeaseBuyers] = useState<Array<{ value: number; label: string }>>([]);
    const [countyList, setCountyList] = useState<County[]>([]);

    useEffect(() => {
        if (props.contactNote !== undefined) {
            setContactNote(props.contactNote);
        }
        if (props.projectStaff !== undefined) {
            setLeaseBuyers(        
                props.projectStaff && props.projectStaff.items.length > 0
                ? props.projectStaff.items
                    .map((landman: AnyObject) => {
                        return { value: landman.id, label: landman.user.firstName + ' ' + landman.user.lastName };
                    })
                : []
            );
        }
    }, [props]);

    useEffect(() => {
        if (props.ownership?.id > 0) {
            getOwnerDetail();
        }
    }, [props.ownership]);

    useEffect(() => {
        if (ownerDetail?.tracts?.length > 0) {
            let initCounty: County[] = [];
            for (let t of ownerDetail.tracts) {
                if (!!t.county && initCounty.indexOf(t.county) === -1) {
                    initCounty.push(t.county);
                }
            }
            setCountyList(initCounty);
        }
    }, [ownerDetail]);

    const getOwnerDetail = async () => {
        let ownerLookupId =
            props.ownership != null ? props.ownership?.ownerLookUpId : 0;

        const result: LeaseGenerationOwnerData = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leasegeneration/owner/${ownerLookupId}`
        );

        if (response.ok) {
            setOwnerDetail(result);
            if (props.contactNote === undefined) {
                setContactNote(result.contactNotes?.find((x) => x.ownershipId === props.ownership?.id));
            }

            const currentContactNoteOwnershipId = result.contactNotes?.find((x) => x.ownershipId === props.ownership?.id)?.id;
            if (currentContactNoteOwnershipId !== undefined) {
                let ownershipIdsTemp = result.contactNotes.filter((x) => x.id === currentContactNoteOwnershipId).map((x) => x.ownershipId);
                if (!!ownershipIdsTemp.every((s) => s === 0)) {
                    setSelectedOwnershipIds([]);
                } else {
                    setSelectedOwnershipIds(result.contactNotes.filter((x) => x.id === currentContactNoteOwnershipId).map((x) => x.ownershipId));
                }
            }
        }
    };

    const onUpdateSelectedOwnershipIds = (ownershipId: number) => {
        let newSelectedOwnershipIds = [];
        if (selectedOwnershipIds.indexOf(ownershipId) > -1) {
            newSelectedOwnershipIds = selectedOwnershipIds.filter((x) => x !== ownershipId);
        } else {
            newSelectedOwnershipIds = [...selectedOwnershipIds, ownershipId];
        }
        setSelectedOwnershipIds(newSelectedOwnershipIds);
    };

    const onSelectAllOwnerships = (selectAll: boolean) => {
        if (selectAll === true) {
            let allIds = ownerDetail.tracts.map(tr => tr.ownershipId);
            setSelectedOwnershipIds(allIds);
        } else {
            setSelectedOwnershipIds([]);
        }
    };

    // CONTACT NOTE METHODS
    const initializeContactNote = (): ContactNote => {
        const blankContactNote: ContactNote = {
            id: 0,
            note: null,
            ownershipId: null,
            modifiedById: 0,
            modifiedDate: new Date(),
            createdById: 0,
            createdDate: new Date(),
        };
        if (!!props.contactNote && ownerDetail?.contactNotes) {
            const contactNotesForCurrentOwnership = ownerDetail.contactNotes.filter(
                (x) => x.id === props.contactNote.id
            );

            setSelectedOwnershipIds(contactNotesForCurrentOwnership.map((x) => x.ownershipId));

            setContactNote(props.contactNote);
            return props.contactNote;
        }
        if (ownerDetail?.contactNotes) {
            const contactNotesForCurrentOwnership = ownerDetail.contactNotes.filter(
                (x) => x.ownershipId === props.ownership?.id
            );

            setSelectedOwnershipIds(contactNotesForCurrentOwnership.map((x) => x.ownershipId));

            setContactNote(contactNotesForCurrentOwnership[0] || blankContactNote);
            return contactNotesForCurrentOwnership[0] || blankContactNote;
        }
        setContactNote(blankContactNote);
        return blankContactNote;
    };

    const onSubmitContactNote = (data: ContactNote, event: React.FormEvent<HTMLFormElement>) => {
        event.stopPropagation();

        data = finalizeContactNote(data);

        if (data.id === 0) {
            data.createdDate = new Date();
            data.createdById = Number(appContext.currentUser.userId);

            createContactNote(data);
        } else {
            updateContactNote(data);
        }
    };

    const finalizeContactNote = (data: ContactNote) => {
        data.id = Number(contactNote?.id || 0);

        data.modifiedDate = new Date();
        data.modifiedById = Number(appContext.currentUser.userId);

        data.ownershipId = props.ownership?.id;
        data.ownershipIds = selectedOwnershipIds;

        return data;
    };

    const createContactNote = async (newContactNote: ContactNote) => {
        if (props.ownership != null) {
            await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contact-notes`, newContactNote).then(() =>
                handleSuccessfulSave()
            );
        }

        if (response.ok) {
            setSaveComplete(true);
            setTimeout(() => setSaveComplete(false), 2000);
            props.onCloseDialog();
        }
    };

    const updateContactNote = async (newContactNote: ContactNote) => {
        if (props.ownership != null) {
            await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contact-notes`, newContactNote).then(() =>
                handleSuccessfulSave()
            );
        }

        if (response.ok) {
            setSaveComplete(true);
            setTimeout(() => setSaveComplete(false), 2000);
            props.onCloseDialog();
        } else {
            console.log('error on the put', response);
        }
    };

    // LEASE BUYER AND CONTACT CATEGORY METHODS
    const onSubmitLeaseBuyer = (data: { leaseBuyer: number }, event: React.FormEvent<HTMLFormElement>) => {
        event.stopPropagation();
        let savedNumber = 1;

        selectedOwnershipIds.forEach(id => {
            let ownership = ownerDetail.ownerships.find(o => o.id === id);

            ownership.leaseBuyer = parseInt(data.leaseBuyer + '', 10);

            updateOwnership(ownership, savedNumber);
            savedNumber += 1;
        });
    };

    const onSubmitContactCategory = (data: { contactCategory: number }, event: React.FormEvent<HTMLFormElement>) => {
        event.stopPropagation();
        let savedNumber = 1;

        selectedOwnershipIds.forEach(id => {
            let ownership = ownerDetail.ownerships.find(o => o.id === id);

            ownership.ownershipContactCategoryId = parseInt(data.contactCategory + '', 10);

            updateOwnership(ownership, savedNumber);
            savedNumber += 1;
        });
    };

    const updateOwnership = async (ownership: Ownership, savedNumber: number) => {
        // TODO: The save-ownership-detail endpoint is very messy. This will work, but the tract and field will always be undefined here.
        await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/tracts/save-ownership-detail`, ownership);

        if (response.ok && savedNumber === selectedOwnershipIds.length) {
            handleSuccessfulSave(ownership.leaseBuyer);
            setSaveComplete(true);
            setTimeout(() => setSaveComplete(false), 2000);
        }
    };

    // HANDLE SAVES
    const handleSuccessfulSave = (leaseBuyer: number = undefined) => {
        if (props.refreshContainer) {
            props.refreshContainer();
        }

        if (props.context === 'Lease Buyers' && leaseBuyer > 0) {
            let staff = props.projectStaff?.items;
            let lbList: OwnershipLeaseBuyer[] = [];
            
            for (let oId of selectedOwnershipIds) {
                let tDetails = ownerDetail.tracts.find(tr => tr.ownershipId === oId);
                let staffed = staff.find(ps => ps.id === leaseBuyer);
                let gross = tDetails.grossAcres;
                let netAcres = !(isNaN(gross) || isNaN(tDetails.decimalInterest)) ? gross * tDetails.decimalInterest : 0;
                let county = countyList.find(c => c.id === tDetails.countyId);
                let minCat = netAcres < 1 ? 'Less than one acre' : `${Math.floor(netAcres)} acre(s)`;
    
                let lb: OwnershipLeaseBuyerForm = {
                    leaseBuyerEmail: staffed?.user.username,
                    ccEmail: '',
                    lessor: `${ownerDetail.ownerDetails.name}`,
                    globalId: `${appContext.clientNumber}.${appContext.projectNumber}.${tDetails.sectionNumber}.${tDetails.tractNumber}-${tDetails.horizonNumber}`,
                    tractDescription: `Tr ${tDetails.tractNumber}, Hz ${tDetails.horizonNumber}`,
                    grossAcres: !isNaN(gross) ? gross : 0,
                    netAcres: netAcres,
                    county: !!county ? county.name : 'N/A',
                    status: tDetails.leasedStatus,
                    addlOwners: !!tDetails.additionalOwnerships ? tDetails.additionalOwnerships.length : 0,
                    mineralCategory: `Mineral Owners ${minCat}`,
                    assignment: 'New Lease',
                    sendEmail: false
                };
    
                lbList.push(lb);
            }
    
            props.handleSendList(lbList);
        } else {
            props.onCloseDialog();
        }
    };
    
    return (
        <Dialog title={props.context}>
            <div style={{ display: 'flex', width: '60vw' }}>
                {props.context === 'Contact Notes' && (
                    <ContactNoteForm
                        contactNote={contactNote}
                        onSubmit={onSubmitContactNote}
                        saveComplete={saveComplete}
                        selectedOwnershipIds={selectedOwnershipIds}
                        ownerDetail={ownerDetail}
                        initializeContactNote={initializeContactNote}
                    />
                )}
                {props.context === 'Lease Buyers' && (
                    <LeaseBuyerForm
                        selectedOwnershipIds={selectedOwnershipIds}
                        ownerDetail={ownerDetail}
                        ownership={props.ownership}
                        leaseBuyers={leaseBuyers}
                        onSubmit={onSubmitLeaseBuyer}
                        saveComplete={saveComplete}
                    />
                )}
                {props.context === 'Contact Categories' && (
                    <ContactCategoryForm
                        selectedOwnershipIds={selectedOwnershipIds}
                        ownerDetail={ownerDetail}
                        ownership={props.ownership}
                        contactCategories={props.contactCategories.categories}
                        onSubmit={onSubmitContactCategory}
                        saveComplete={saveComplete}
                    />
                )}
                <div style={{ margin: '.5rem', width: '50%' }}>
                    {ownerDetail?.tracts && (
                        <>
                            <div>Tract List</div>
                            <div style={{ maxHeight: '40rem', overflowY: 'auto', overscrollBehavior: 'contain', paddingTop: '.5rem' }}>
                                <OwnershipTractList
                                    leaseBuyers={leaseBuyers}
                                    contactCategories={props.contactCategories}
                                    tracts={ownerDetail?.tracts}
                                    clientNumber={appContext.clientNumber}
                                    projectNumber={appContext.projectNumber}
                                    selectedOwnershipIds={selectedOwnershipIds}
                                    projectType={props.projectType}
                                    updateSelectedOwnershipIds={onUpdateSelectedOwnershipIds}
                                    selectAllOwnerships={onSelectAllOwnerships}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default ManageOwnershipDialog;


// WEBPACK FOOTER //
// ./src/components/project/sections/tract/dialogs/ManageOwnershipDialog.tsx